import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  fetchAllMeters,
  fetchFieldOfficers,
  fetchMeterMonthlyReport,
  fetchMeterWidgets,
  getActiveFieldOfficers,
  humanizeError,
  normalizeMeter,
} from '../services/api';
import Dropdown from '../components/global/Dropdown';
import Button from '../components/global/Button';
import { useSearch } from '../context/SearchContext';
import { useAuth } from '../context/AuthContext';
import CardWidget from '../features/dashboard/components/configs/widgets/CardWidget';
import BarChart from '../components/charts/Barchart';
import PieChart from '../components/charts/PieChart';
import FiltersBar from '../components/dashboard/FiltersBar';
import RecentActivityCard from '../components/dashboard/RecentActivityCard';
import MonthlyInstalledMetersCard from '../components/dashboard/MonthlyInstalledMetersCard';
import exportIcon from '../assets/icons/ExportIcon.svg';
import {
  clientListExists,
  getSortedClientOptions,
  getSortedProjectOptions,
} from '../utils/clientProjectSelectOptions';
import './Dashboard.css';

const KPI_ROUTES = {
  'Total Meters': '/meters-installed',
  Accepted: '/accepted',
  'Pending To Verify': '/pending-to-verify',
  Rejected: '/rejected',
  'Field Officers': '/field-officers',
};

export default function Dashboard() {
  const [meters, setMeters] = useState([]);
  const [officers, setOfficers] = useState({});
  const [widgets, setWidgets] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterDraft, setFilterDraft] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const { query: searchQuery } = useSearch();
  const [reportRange, setReportRange] = useState('Year'); // 'Month' | 'Week' | 'Year'
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const { user } = useAuth();

  const clientOptions = useMemo(() => getSortedClientOptions(user), [user]);

  const projectOptions = useMemo(
    () => getSortedProjectOptions(user, selectedClientId),
    [user, selectedClientId],
  );

  const projectNeedsClientFirst = clientListExists(user) && !selectedClientId;
  const noProjectsForClient =
    Boolean(selectedClientId) && clientListExists(user) && projectOptions.length === 0;

  const clientDropdownOptions = useMemo(
    () => clientOptions.map((c) => ({ value: c.id, label: c.label })),
    [clientOptions],
  );

  const projectDropdownOptions = useMemo(() => {
    if (projectNeedsClientFirst || noProjectsForClient) {
      return [];
    }
    return projectOptions.map((p) => ({ value: p.id, label: p.label }));
  }, [projectNeedsClientFirst, noProjectsForClient, projectOptions]);

  useEffect(() => {
    if (!selectedProjectId) return;
    if (!projectOptions.some((p) => String(p.id) === String(selectedProjectId))) {
      setSelectedProjectId('');
    }
  }, [projectOptions, selectedProjectId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [widgetData, officerMap] = await Promise.all([
        fetchMeterWidgets(),
        fetchFieldOfficers(),
      ]);
      setWidgets(widgetData);
      setOfficers(officerMap);
      setLoading(false);

      const rawMeters = await fetchAllMeters();
      setMeters(rawMeters.map((m) => normalizeMeter(m, officerMap)));
    } catch (err) {
      setError(humanizeError(err, 'We couldn\u2019t load the dashboard right now.'));
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    fetchMeterMonthlyReport({
      range: reportRange,
      projectId: selectedProjectId || undefined,
    })
      .then((data) => {
        if (!cancelled) setMonthlyReport(data);
      })
      .catch(() => {
        if (!cancelled) setMonthlyReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [reportRange, selectedProjectId]);

  const setFilter = (key, value) => {
    setFilterDraft((prev) => {
      const next = { ...prev };
      if (!value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filterDraft });
  }, [filterDraft]);

  const clearAll = () => {
    setFilterDraft({});
    setAppliedFilters({});
  };
  const hasFilters =
    Object.keys(filterDraft).length > 0 || Object.keys(appliedFilters).length > 0;

  const filterOptions = useMemo(() => {
    const unique = (key) => [...new Set(meters.map((m) => m[key]).filter(Boolean))].sort();
    return [
      { key: 'state', label: 'State', allLabel: 'All States', options: unique('state') },
      { key: 'depot', label: 'Depot', allLabel: 'All Depots', options: unique('depot') },
      { key: 'area', label: 'Area', allLabel: 'All Areas', options: unique('area') },
      { key: 'qtr', label: 'Qtr No', allLabel: 'All Qtr No', options: unique('qtr') },
      { key: 'status', label: 'Status', allLabel: 'All Statuses', options: unique('status') },
      { key: 'type', label: 'Type', allLabel: 'All Types', options: unique('type') },
      { key: 'officer', label: 'Field Officer', allLabel: 'All Officers', options: unique('officer').filter((o) => o !== '—') },
    ];
  }, [meters]);

  const filtered = useMemo(() => {
    let data = [...meters];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter((row) =>
        row.meter.toLowerCase().includes(q) ||
        row.qtr.toLowerCase().includes(q) ||
        row.area.toLowerCase().includes(q) ||
        row.depot.toLowerCase().includes(q) ||
        row.state.toLowerCase().includes(q) ||
        row.officer.toLowerCase().includes(q)
      );
    }
    Object.entries(appliedFilters).forEach(([key, val]) => {
      data = data.filter((row) => row[key] === val);
    });
    return data;
  }, [meters, appliedFilters, searchQuery]);

  /* ── summary stats (aligned with filters + KPI spark data) ── */
  const activeOfficers = getActiveFieldOfficers(officers);

  const stats = useMemo(() => {
    const fromMeters = meters.length > 0;
    const totalCount = fromMeters
      ? filtered.length
      : Number(widgets?.metersInstalled) || 0;
    const acceptedCount = fromMeters
      ? filtered.filter((m) => m.status === 'Accepted').length
      : Number(widgets?.metersApproved) || 0;
    const pendingCount = fromMeters
      ? filtered.filter((m) => m.status === 'Pending').length
      : Number(widgets?.pendingApprovals) || 0;
    const rejectedCount = fromMeters
      ? filtered.filter((m) => m.status === 'Rejected').length
      : Number(widgets?.metersRejected) || 0;
    const stateCount = fromMeters
      ? new Set(filtered.map((m) => m.state)).size
      : 0;
    const pct = (n) => (totalCount ? Math.round((n / totalCount) * 100) : 0);
    return [
      { label: 'Total Meters', value: totalCount, sub: fromMeters ? `${stateCount} states covered` : 'Loading details…', icon: '⚡', color: '#4318ff' },
      { label: 'Accepted', value: acceptedCount, sub: `${pct(acceptedCount)}% of total`, icon: '✓', color: '#01b574' },
      { label: 'Pending To Verify', value: pendingCount, sub: `${pct(pendingCount)}% awaiting`, icon: '⏳', color: '#ffb547' },
      { label: 'Rejected', value: rejectedCount, sub: `${pct(rejectedCount)}% rejected`, icon: '✕', color: '#e31a1a' },
      { label: 'Field Officers', value: activeOfficers.length, sub: 'Active in system', icon: '👷', color: '#7551ff' },
    ];
  }, [filtered, activeOfficers, meters.length, widgets]);

  const quickReportData = useMemo(() => {
    const safeDate = (m) => {
      const raw = m?.dateRaw || m?.date;
      const iso = raw ? String(raw).slice(0, 10) : '';
      // expect YYYY-MM-DD
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
      const d = new Date(`${iso}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const inRange = (d, start, end) => d && d >= start && d <= end;
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

    let xAxisData = [];
    let start = null;

    if (reportRange === 'Week') {
      start = new Date(end);
      start.setDate(start.getDate() - 6);
      xAxisData = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      });
    } else if (reportRange === 'Month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1, 0, 0, 0, 0);
      const daysInMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
      xAxisData = Array.from({ length: daysInMonth }).map((_, i) => String(i + 1));
    } else {
      // Year (month-wise)
      start = new Date(end.getFullYear(), 0, 1, 0, 0, 0, 0);
      xAxisData = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    }

    const installed = new Array(xAxisData.length).fill(0);
    const accepted = new Array(xAxisData.length).fill(0);
    const rejected = new Array(xAxisData.length).fill(0);
    const pending = new Array(xAxisData.length).fill(0);

    const bucketIndex = (d) => {
      if (reportRange === 'Week') {
        const diffDays = Math.floor((d - start) / (24 * 60 * 60 * 1000));
        return diffDays >= 0 && diffDays < 7 ? diffDays : -1;
      }
      if (reportRange === 'Month') return d.getMonth() === end.getMonth() && d.getFullYear() === end.getFullYear() ? d.getDate() - 1 : -1;
      return d.getFullYear() === end.getFullYear() ? d.getMonth() : -1;
    };

    filtered.forEach((m) => {
      const d = safeDate(m);
      if (!d) return;
      if (!inRange(d, start, end)) return;
      const idx = bucketIndex(d);
      if (idx < 0) return;

      installed[idx] += 1;
      if (m.status === 'Accepted') accepted[idx] += 1;
      if (m.status === 'Rejected') rejected[idx] += 1;
      if (m.status === 'Pending') pending[idx] += 1;
    });

    const hasAnyValue =
      installed.some((v) => v > 0) ||
      accepted.some((v) => v > 0) ||
      rejected.some((v) => v > 0) ||
      pending.some((v) => v > 0);
    if (!hasAnyValue) return null;

    return {
      xAxisData,
      seriesData: [
        { name: 'Meter Installed', data: installed },
        { name: 'Pending To Verify', data: pending },
        { name: 'Commission Accepted', data: accepted },
        { name: 'Commission Rejected', data: rejected },
      ],
      kpiSeries: { installed, accepted, pending, rejected },
    };
  }, [filtered, reportRange]);

  const quickReportDataWithDummy = useMemo(() => {
    if (quickReportData) return quickReportData;

    if (monthlyReport) {
      const installed = monthlyReport.installed || [];
      const accepted = monthlyReport.approved || [];
      const rejected = monthlyReport.rejected || [];
      const pending = monthlyReport.pending || [];
      return {
        xAxisData: monthlyReport.labels || [],
        seriesData: [
          { name: 'Meter Installed', data: installed },
          { name: 'Pending To Verify', data: pending },
          { name: 'Commission Accepted', data: accepted },
          { name: 'Commission Rejected', data: rejected },
        ],
        kpiSeries: { installed, accepted, pending, rejected },
      };
    }

    // Temporary dummy data for design alignment when real data is empty.
    if (reportRange === 'Week') {
      const inst = [8, 15, 12, 0, 5, 16, 0];
      const acc = [6, 10, 9, 0, 4, 12, 0];
      const rej = [2, 5, 3, 0, 1, 4, 0];
      const pend = inst.map((v, i) => Math.max(0, v - (acc[i] || 0) - (rej[i] || 0)));
      return {
        xAxisData: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        seriesData: [
          { name: 'Meter Installed', data: inst },
          { name: 'Pending To Verify', data: pend },
          { name: 'Commission Accepted', data: acc },
          { name: 'Commission Rejected', data: rej },
        ],
        kpiSeries: { installed: inst, accepted: acc, pending: pend, rejected: rej },
      };
    }

    if (reportRange === 'Month') {
      // 30-day month dummy
      const days = Array.from({ length: 30 }).map((_, i) => String(i + 1));
      const installed = days.map((_, i) => (i % 7 === 0 ? 0 : 6 + (i % 5)));
      const accepted = installed.map((v) => Math.max(0, Math.floor(v * 0.75)));
      const rejected = installed.map((v) => Math.max(0, v - Math.floor(v * 0.75)));
      const pending = installed.map((v, i) => Math.max(0, v - (accepted[i] || 0) - (rejected[i] || 0)));
      return {
        xAxisData: days,
        seriesData: [
          { name: 'Meter Installed', data: installed },
          { name: 'Pending To Verify', data: pending },
          { name: 'Commission Accepted', data: accepted },
          { name: 'Commission Rejected', data: rejected },
        ],
        kpiSeries: { installed, accepted, pending, rejected },
      };
    }

    // Year dummy (month-wise)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const installed = [7200, 7000, 6900, 7100, 7050, 7150, 7100, 7200, 7250, 7300, 7350, 7400];
    const accepted = installed.map((v) => Math.floor(v * 0.8));
    const rejected = installed.map((v) => Math.max(0, v - Math.floor(v * 0.8)));
    const pending = installed.map((v, i) => Math.max(0, v - (accepted[i] || 0) - (rejected[i] || 0)));
    return {
      xAxisData: months,
      seriesData: [
        { name: 'Meter Installed', data: installed },
        { name: 'Pending To Verify', data: pending },
        { name: 'Commission Accepted', data: accepted },
        { name: 'Commission Rejected', data: rejected },
      ],
      kpiSeries: { installed, accepted, pending, rejected },
    };
  }, [quickReportData, monthlyReport, reportRange]);

  const kpiItems = useMemo(() => {
    const qr = quickReportDataWithDummy;
    const ks = qr?.kpiSeries;
    const n = qr?.xAxisData?.length ?? 0;
    if (!n || !ks) {
      return stats.map((s) => ({
        id: s.label,
        title: s.label,
        value: s.value,
        bottomText: s.sub,
        graphData: null,
        graphAriaLabel: `${s.label} trend for selected report range (Week/Month/Year)`,
        to: KPI_ROUTES[s.label],
      }));
    }
    const { installed, accepted, pending, rejected } = ks;
    const officerSpark = Array.from({ length: n }, () => activeOfficers.length);

    const byLabel = {
      'Total Meters': installed,
      Accepted: accepted,
      'Pending To Verify': pending,
      Rejected: rejected,
      'Field Officers': officerSpark,
    };

    return stats.map((s) => ({
      id: s.label,
      title: s.label,
      value: s.value,
      bottomText: s.sub,
      graphData: byLabel[s.label] ?? null,
      graphAriaLabel: `${s.label} trend`,
      to: KPI_ROUTES[s.label],
    }));
  }, [stats, quickReportDataWithDummy, activeOfficers]);

  const monthlyInstalledSeries = useMemo(() => {
    const labels = quickReportDataWithDummy?.xAxisData || [];
    const values = quickReportDataWithDummy?.seriesData?.[0]?.data || [];
    return { labels, values };
  }, [quickReportDataWithDummy]);

  // System Connectivity pie chart — three status slices sum to total installed,
  // which the donut renders in the centre automatically.
  const connectivityData = useMemo(() => {
    const fromMeters = meters.length > 0;
    const pending = fromMeters
      ? filtered.filter((m) => m.status === 'Pending').length
      : Number(widgets?.pendingApprovals) || 0;
    const accepted = fromMeters
      ? filtered.filter((m) => m.status === 'Accepted').length
      : Number(widgets?.metersApproved) || 0;
    const rejected = fromMeters
      ? filtered.filter((m) => m.status === 'Rejected').length
      : Number(widgets?.metersRejected) || 0;
    return [
      { name: 'Pending to Verify', value: pending },
      { name: 'Accepted', value: accepted },
      { name: 'Rejected', value: rejected },
    ];
  }, [meters.length, filtered, widgets]);

  const handleExport = () => {
    const headers = ['Sl. No', 'Meter Number', 'Qtr No', 'Area', 'Depot', 'State', 'Type', 'Phase', 'Installation Date', 'Field Officer', 'Status', 'Replacement', 'Old Meter', 'Old Reading'];
    const rows = filtered.map((r, i) => [
      i + 1, r.meter, r.qtr, r.area, r.depot, r.state, r.type, r.phase,
      r.date, r.officer, r.status, r.isReplacement ? 'Yes' : 'No', r.oldMeterNumber || '', r.oldMeterReading || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = new Date().toISOString().slice(0, 10);
    a.download = `meters_export_${suffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── render ── */
  if (loading && !widgets) {
    return (
      <div className="dashboard">
        <div className="meter-data-loading">
          <div className="loading-spinner" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !widgets) {
    return (
      <div className="dashboard">
        <div className="meter-data-error">
          <p>Failed to load data: {error}</p>
          <Button className="retry-load-button" onClick={loadData}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-page-header">
        <div>
          <h2 className="dashboard-page-title">Dashboard</h2>
          <p className="dashboard-page-subtitle">Operational Overview</p>
        </div>
        <div className="dashboard-header-toolbar">
          <div className="filter-dropdown-outer header-dropdown-outer">
            <Dropdown
              options={clientDropdownOptions}
              value={selectedClientId}
              onChange={(next) => {
                setSelectedClientId(next == null || next === '' ? '' : String(next));
                setSelectedProjectId('');
              }}
              placeholder="Select client"
              ariaLabel="Select client"
              rootClassName="header-scope-dropdown"
            />
          </div>
          <div className="filter-dropdown-outer header-dropdown-outer">
            <Dropdown
              options={projectDropdownOptions}
              value={selectedProjectId}
              onChange={(next) => setSelectedProjectId(next == null || next === '' ? '' : String(next))}
              disabled={projectNeedsClientFirst || noProjectsForClient}
              placeholder={
                projectNeedsClientFirst
                  ? 'Select client first'
                  : noProjectsForClient
                    ? 'No projects for this client'
                    : 'Select project'
              }
              ariaLabel="Select project"
              rootClassName="header-scope-dropdown"
            />
          </div>
          <Button type="button" className="export-meter-csv-button" onClick={handleExport}>
            <img className="export-meter-csv-button-icon" src={exportIcon} alt="" aria-hidden="true" />
            <span className="export-meter-csv-button-label">Export</span>
          </Button>
        </div>
      </header>

      <FiltersBar
        filters={filterDraft}
        filterOptions={filterOptions}
        onSetFilter={setFilter}
        onGetData={() => {
          applyFilters();
          loadData();
        }}
        onReset={clearAll}
        hasFilters={hasFilters}
      />

      {/* Stat Cards */}
      <CardWidget items={kpiItems} />

      {/* Quick Monthly Report (like Inventory screenshot) */}
      <section className="commission-trend-card" aria-labelledby="commission-trend-heading">
        <div className="commission-trend-header">
          <div>
            <h3 id="commission-trend-heading" className="commission-trend-title">
              Quick Monthly Report
            </h3>
          </div>
          <div className="commission-trend-toolbar">
            <div className="commission-trend-period-tabs" role="tablist" aria-label="Report period">
              {['Month', 'Week', 'Year'].map((r) => {
                const isActive = reportRange === r;
                return (
                  <button
                    key={r}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`commission-trend-period-tab${isActive ? ' is-selected' : ''}`}
                    onClick={() => setReportRange(r)}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <BarChart
          data={quickReportDataWithDummy}
          defaultHeight={320}
          showLegendInteractions={false}
          ariaLabel="Quick monthly report bar chart"
          seriesColors={['#0f2e6e', '#ffb547', '#01b574', '#e31a1a']}
          timeRange={reportRange}
          showTimeRangeInTooltip={false}
        />
      </section>

      <div className="dashboard-insights-columns">
        <section className="geo-deployment-card" aria-labelledby="geo-deployment-heading">
          <div className="geo-deployment-inner">
            <div className="geo-deployment-shell">
            <aside className="geo-deployment-sidebar">
              <h3 id="geo-deployment-heading" className="geo-deployment-heading">
                Regional Deployment
              </h3>
              <p className="geo-deployment-description">
                Geospatial overview of installation density and operational coverage across key regions.
              </p>
              <div className="geo-deployment-map-placeholder" aria-hidden="true" />
            </aside>
            <div className="geo-deployment-main">
              <div className="top-regions-heading-row">
                <span className="top-regions-section-label">Top Regions</span>
              </div>
              <div className="top-regions-table">
                <div className="top-regions-row top-regions-column-labels">
                  <span>Region</span>
                  <span>Installed</span>
                  <span>Officers</span>
                  <span>Status</span>
                </div>
                {[
                  { region: 'Bangalore North', installed: 428, officers: 12, status: 'Stable' },
                  { region: 'Chennai Central', installed: 312, officers: 8, status: 'Stable' },
                  { region: 'Hyderabad West', installed: 285, officers: 7, status: 'Alert' },
                  { region: 'Mumbai East', installed: 241, officers: 6, status: 'Stable' },
                  { region: 'Pune South', installed: 198, officers: 5, status: 'Stable' },
                  { region: 'Pune South', installed: 198, officers: 5, status: 'Stable' },
                  { region: 'Pune South', installed: 198, officers: 5, status: 'Stable' },
                ].map((r, i) => (
                  <div key={`${r.region}-${i}`} className="top-regions-row">
                    <span className="top-regions-cell top-regions-name">{r.region}</span>
                    <span className="top-regions-cell top-regions-cell--num">{r.installed}</span>
                    <span className="top-regions-cell top-regions-cell--num">{r.officers}</span>
                    <span className="top-regions-cell top-regions-cell--status">
                      <span
                        className={`region-status-pill${r.status === 'Alert' ? ' region-status-pill--alert' : ' region-status-pill--stable'}`}
                      >
                        <span className="region-status-pill__dot" aria-hidden />
                        {r.status.toUpperCase()}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
            </div>
          </div>
        </section>

        <section className="connectivity-summary-card" aria-labelledby="connectivity-summary-heading">
          <h3 id="connectivity-summary-heading" className="connectivity-summary-title">
            System Connectivity
          </h3>
          <div className="connectivity-summary-chart">
            <PieChart
              ariaLabel="System connectivity donut chart"
              height={340}
              legendLayout="bottom"
              seriesName="Connectivity"
              data={connectivityData}
              colors={['#ffb547', '#01b574', '#e31a1a']}
              centerColor="#0f2e6e"
              centerLabel="Total Installed"
              showLegend
            />
          </div>
        </section>
      </div>

      <div className="dashboard-widgets-row">
        <RecentActivityCard />
        <MonthlyInstalledMetersCard
          labels={monthlyInstalledSeries.labels}
          values={monthlyInstalledSeries.values}
        />
      </div>
    </div>
  );
}
